/**
 * @license
 * Copyright© Celligence International, LLC - 2023
 * This software program is protected by copyright laws and is the property of
 * Celligence International, LLC (Celligence). Any unauthorized reproduction,
 * distribution, modification, or public display of this program, without the
 * explicit permission of Celligence is strictly prohibited. If you have gained
 * access to this program without the expressed approval of Celligence report
 * the incident to legal@celligence.com.
*/
import React, { lazy, Suspense, useEffect, useState } from "react";
import "@fontsource/poppins";
import "../src/App.css";
import axios from "axios";
import { Spinner } from "react-bootstrap";
import Header from "./components/Header/HeaderNav";
import CacheBuster from "react-cache-buster";
import appversion from "../package.json";
import aibrokerlocaldata from "./aibroker.json";
import Banner from "./components/banner/banner.js";
import { CookieSetting } from "cookiesetting-component";

const About = lazy(() => import("./components/about/about.js"));
const JoinSWMC = lazy(() => import("./components/JoinandValue/JoinandValue.js"));
const Table = lazy(() => import("./components/Table/table.js"));
const Angelai = lazy(() => import("./components/Angelai/angelai.js"));
const Tru = lazy(() => import("./components/Tru/tru.js"));
const Moretech = lazy(() => import("./components/Moretech/moretech.js"));
const Products = lazy(() => import("./components/Products/products.js"));
const Presence = lazy(() => import("./components/Presence/presence.js"));
const Events = lazy(() => import("./components/Events/events.js"));
const Articles = lazy(() => import("./components/Articles/articles.js"));
const Badges = lazy(() => import("./components/Badges/badges.js"));
const Training = lazy(() => import("./components/Training/training.js"));
const Fairlending = lazy(() => import("./components/Fairlending/fairlending.js"));
const Contact = lazy(() => import("./components/Contact/contact.js"));
const Footer = lazy(() => import("./components/Footer/footer.js"));

function App() {
  const [isMobile, setIsMobile] = useState(false);
  const [Loading, setLoading] = useState(true);
  const [HeaderData, setHeaderData] = useState();
  const [BannerData, setBannerData] = useState();
  const [AboutData, setAboutData] = useState();
  const [JoinandValueData, setJoinandValueData] = useState();
  const [TableData, setTableData] = useState();
  const [AngelaiData, setAngelaiData] = useState();
  const [TruData, setTruData] = useState();
  const [MoretechData, setMoretechData] = useState();
  const [ProductsData, setProductsData] = useState();
  const [PresenceData, setPresenceData] = useState();
  const [EventsData, setEventsData] = useState();
  const [ArticlesData, setArticlesData] = useState();
  const [BadgesData, setBadgesData] = useState();
  const [TrainingData, setTrainingData] = useState();
  const [FairlendingData, setFairlendingData] = useState();
  const [FooterData, setFooterData] = useState();

  useEffect(() => {
    const fetchAiiaJSON = async () => {
      try {
        const url = process.env.REACT_APP_CDN_URL + "aibroker/aibroker.json"; // Use the CDN URL from environment variables
        const response = await axios.get(url);
        const aibrokerJsonData = response.data;
        setHeaderData(aibrokerJsonData.Header);
        setBannerData(aibrokerJsonData.Banner);
        setAboutData(aibrokerJsonData.About);
        setJoinandValueData(aibrokerJsonData.joinSWMC);
        setTableData(aibrokerJsonData.table);
        setAngelaiData(aibrokerJsonData.angelAi);
        setTruData(aibrokerJsonData.truapproval);
        setMoretechData(aibrokerJsonData.moreTech);
        setProductsData(aibrokerJsonData.products);
        setPresenceData(aibrokerJsonData.presence);
        setEventsData(aibrokerJsonData.events);
        setArticlesData(aibrokerJsonData.articles);
        setBadgesData(aibrokerJsonData.badges);
        setTrainingData(aibrokerJsonData.training);
        setFairlendingData(aibrokerJsonData.fairLending);
        setFooterData(aibrokerJsonData.Footer);
        setLoading(false);
      } catch (error) {
        console.log("Failed to fetch JSON from CDN:", error);
        // If there's an error fetching the JSON from the CDN, use the local JSON data instead
        setHeaderData(aibrokerlocaldata.Header);
        setBannerData(aibrokerlocaldata.Banner);
        setAboutData(aibrokerlocaldata.About);
        setJoinandValueData(aibrokerlocaldata.joinSWMC);
        setTableData(aibrokerlocaldata.table);
        setAngelaiData(aibrokerlocaldata.angelAi);
        setTruData(aibrokerlocaldata.truapproval);
        setMoretechData(aibrokerlocaldata.moreTech);
        setProductsData(aibrokerlocaldata.products);
        setPresenceData(aibrokerlocaldata.presence);
        setEventsData(aibrokerlocaldata.events);
        setArticlesData(aibrokerlocaldata.articles);
        setBadgesData(aibrokerlocaldata.badges);
        setTrainingData(aibrokerlocaldata.training);
        setFairlendingData(aibrokerlocaldata.fairLending);
        setFooterData(aibrokerlocaldata.Footer);
        setLoading(false);
      }
    };

    fetchAiiaJSON();
  }, []);

  useEffect(() => {
    if(window.innerWidth>=992) {
      setIsMobile(false);
    } else {
      setIsMobile(true);
    }
  }, []);

  return (
    <CacheBuster
      currentVersion={appversion.version}
      isEnabled={true} //If false, the library is disabled.
      isVerboseMode={false} //If true, the library writes verbose logs to console.
      // loadingComponent={<Loading />} //If not pass, nothing appears at the time of new version check.
      metaFileDirectory={process.env.PUBLIC_URL} //If public assets are hosted somewhere other than root on your server.
    >
      <div className="bodyContainer">
        {Loading ? (
          <Spinner animation="border" />
        ) : (
          <>
            <Header fetchpriority="high" HeaderData={HeaderData} isMobile={isMobile} />
            <>
              <Banner fetchpriority="high" BannerData={BannerData} isMobile={isMobile} />
              <Suspense fallback={<div></div>}>
                <About AboutData={AboutData} />
              </Suspense>
              <Suspense fallback={<div></div>}>
                <JoinSWMC JoinandValueData={JoinandValueData} />
              </Suspense>
              <Suspense fallback={<div></div>}>
                <Table TableData={TableData} />
              </Suspense>
              <Suspense fallback={<div></div>}>
                <Angelai AngelaiData={AngelaiData} />
              </Suspense>
              <Suspense fallback={<div></div>}>
                <Tru TruData={TruData} />
              </Suspense>
              <Suspense fallback={<div></div>}>
                <Moretech MoretechData={MoretechData} />
              </Suspense>
              <Suspense fallback={<div></div>}>
                <Products ProductsData={ProductsData} />
              </Suspense>
              <Suspense fallback={<div></div>}>
                <Presence PresenceData={PresenceData} />
              </Suspense>
              <Suspense fallback={<div></div>}>
                <Events EventsData={EventsData} />
              </Suspense>
              <Suspense fallback={<div></div>}>
                <Articles ArticlesData={ArticlesData} />
              </Suspense>
              <Suspense fallback={<div></div>}>
                <Badges BadgesData={BadgesData} />
              </Suspense>
              <Suspense fallback={<div></div>}>
                <Training TrainingData={TrainingData} />
              </Suspense>
              <Suspense fallback={<div></div>}>
                <Fairlending FairlendingData={FairlendingData} />
              </Suspense>
              <Suspense fallback={<div></div>}>
                <Contact />
              </Suspense>
              <Suspense fallback={<div></div>}>
                <Footer FooterData={FooterData} BannerData={BannerData} />
              </Suspense>
              <CookieSetting 
                privacyPolicyLink={process.env.REACT_APP_PRIVACY_POLICY_LINK} 
                configUrl={process.env.REACT_APP_COOKIE_SETTING_JSON}
                analytics={process.env.REACT_APP_ANALYTICS_PATH}
              />
            </>
          </>
        )}

      </div>
    </CacheBuster>
  );
}

export default App;
